import React, { useEffect, useState } from 'react'
import AdminCard from './adminCard'
import { editableMergeField } from '../constants/editableMergeField.constants'
import configService from '../services/config.service'
import MergeFieldSelect from './mergeFieldSelect'
import PropTypes from 'prop-types'
import EventTable from './events/eventTable'
import { eventDateTypes } from '../constants/eventDateTypes.constants'
import { useDispatch } from 'react-redux'
import { eventSetupTypes } from '../constants/eventsSetupTypes.constants'
import { editableMergeFieldEnum } from '../enums/editableMergeFieldEnum'
import { IsFeatureFlagEnabled } from '../providers/globalFeatureFlag.provider'
import { GlobalFeatureFlagConstants } from '../constants/globalFeatureFlag.constants'

const EMAIL_MERGE_FIELD_VALUES = [
    'caseOwnerPersonEmail',
    'caseOwnerHomeEmail',
    'caseOwnerCompanyEmail',
    'employeePersonEmail',
    'employeeHomeEmail',
    'employeeCompanyEmail',
]

const EditableRegionFormMerge = ({
    mergeFields,
    mergeFieldId,
    mergeFieldIdChanged,
    onSelectingEventId,
    editableMergeFieldTypeValue,
}) => {
    const [selectedType, setSelectedType] = useState(null)
    const [selectedEventId, setSelectedEventId] = useState(
        editableMergeFieldTypeValue
    )
    const [
        eventSetupDateTypeLookupId,
        setEventSetupDateTypeLookupId,
    ] = useState(null)
    const [events, setEvents] = useState([])
    const [pageNumber, setPageNumber] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const [sortField, setSortField] = useState('id')
    const [sortDirection, setSortDirection] = useState('asc')
    const isEventSelected = selectedType === editableMergeField.EventType
    const pageSize = 20
    const dispatch = useDispatch()
    const isEmailMergeFieldsEnabled = IsFeatureFlagEnabled(
        GlobalFeatureFlagConstants.DocumentEmailMergeFields_73519
    )

    const dateTypeMap = {
        eventSingleDate: eventDateTypes.singleDate,
        eventFromDualDate: eventDateTypes.dualDate,
        eventToDualDate: eventDateTypes.dualDate,
    }

    const shouldGetData =
        !selectedEventId || eventSetupDateTypeLookupId !== null

    useEffect(() => {
        if (!shouldGetData) return

        const isEnabled = true
        const isDocumentVisible = true

        configService
            .getPagedEvents(
                pageNumber + 1,
                pageSize,
                isEnabled,
                isDocumentVisible,
                sortField,
                sortDirection,
                eventSetupDateTypeLookupId
            )
            .then(response => {
                setEvents(response.data.data)
                setTotalCount(response.data.totalDataCount)
                handleMergeFieldChange(mergeFieldId, mergeFields)

                dispatch({
                    type: eventSetupTypes.SET_EVENT_SETUPS,
                    payload: response.data.data,
                })
            })
    }, [
        pageNumber,
        sortField,
        sortDirection,
        eventSetupDateTypeLookupId,
        selectedEventId,
        dispatch,
        mergeFieldId,
        mergeFields,
    ])

    useEffect(() => {
        handleMergeFieldChange(mergeFieldId, mergeFields)
    }, [mergeFieldId, mergeFields])

    const handleMergeFieldChange = (mergeFieldIdParam, mergeFieldsParam) => {
        if (!mergeFieldIdParam || mergeFieldIdParam === 0) {
            setSelectedType(null)
            setSelectedEventId(null)
            return
        }

        const found = mergeFieldsParam.find(f => f.id === mergeFieldIdParam)
        if (found) {
            setSelectedType(found.type)

            const dateTypeId = dateTypeMap[found.value]
            if (dateTypeId) {
                setEventSetupDateTypeLookupId(dateTypeId)
                setEvents(prev =>
                    prev.filter(
                        c => c.eventSetupDateTypeLookupId === dateTypeId
                    )
                )
            }

            if (found.type !== editableMergeField.EventType) {
                setSelectedEventId(null)
                onSelectingEventId(null)
            }
        } else {
            setSelectedType(null)
            setSelectedEventId(null)
        }
    }

    const visibleMergeFields = isEmailMergeFieldsEnabled
        ? mergeFields
        : mergeFields.filter(
              field => !EMAIL_MERGE_FIELD_VALUES.includes(field.value)
          )

    const groupedFields = visibleMergeFields.reduce((acc, item) => {
        const label = editableMergeFieldEnum[item.type] || 'Unknown'
        if (!acc[label]) acc[label] = []
        acc[label].push(item)
        return acc
    }, {})

    const allFields = Object.values(groupedFields).flat()
    const selectedMergeField = allFields.find(
        f => f.id === mergeFieldId && f.type === selectedType
    ) || { id: 0, type: null }

    const handleSort = field => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    return (
        <AdminCard title={'Merge'}>
            <div>
                <MergeFieldSelect
                    selectedMergeField={selectedMergeField}
                    groupedFields={groupedFields}
                    allFields={allFields}
                    onChange={({ id, type }) => {
                        mergeFieldIdChanged(id)
                        setSelectedType(type)
                    }}
                />

                {isEventSelected && (
                    <EventTable
                        events={events}
                        selectedEventId={selectedEventId}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        pageNumber={pageNumber}
                        totalCount={totalCount}
                        onSelectingEventId={(id, editableRegionTypeLookup) => {
                            setSelectedEventId(id)
                            onSelectingEventId(id, editableRegionTypeLookup)
                        }}
                        pageSize={pageSize}
                        onSort={handleSort}
                        onPageChange={setPageNumber}
                    />
                )}
            </div>
        </AdminCard>
    )
}

EditableRegionFormMerge.propTypes = {
    mergeFields: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            type: PropTypes.number.isRequired,
            value: PropTypes.string.isRequired,
        })
    ).isRequired,
    mergeFieldId: PropTypes.number.isRequired,
    mergeFieldIdChanged: PropTypes.func.isRequired,
    onSelectingEventId: PropTypes.func.isRequired,
    editableMergeFieldTypeValue: PropTypes.string,
}

export default EditableRegionFormMerge
